import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  AppBar, Toolbar, Typography, Box, Button, IconButton,
  Chip, Avatar, Tabs, Tab, Divider, Tooltip, Select, MenuItem,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Assignment as LineupIcon,
  ContentCopy as ContentCopyIcon,
  Psychology as PsychologyIcon,
  LooksOne as LooksOneIcon,
  LooksTwo as LooksTwoIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  DragIndicator as DragIndicatorIcon,
  SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import { SKILL_LABELS } from '../constants';
import {
  LINEUP_POSITIONS, TEAM_COLORS, TEAM_LABELS,
  TOURNAMENT_MAX_PER_POS, TOURNAMENT_MAX_GAMES,
  SWAP_MODES, SWAP_MODE_KEYS, groupLetter, groupColor, newGroupId, getSwapGroups, getUngroupedPlayers,
  slotCaption, getSwapPartnerIndex,
  emptyTrainingLineup, emptyTournamentLineup, emptyGame,
  getLineupMeta, withLineupMeta,
} from '../utils/lineup';
import { skillTotal, getPosFit } from '../utils/players';

const HIDDEN_TEST_USER_EMAILS = new Set(['test@spieler.de', 'test@trainer.de']);
const POSITION_SKILL_KEYS = {
  Sturm: 'sturm',
  Verteidigung: 'verteidigung',
  Deckel: 'deckel',
};
const POSITION_WEIGHT_BONUS = {
  primary: 0.35,
  secondary: 0.15,
  none: 0,
};

export default function LineupDialog({ event, open, onClose, players, canEdit, onSave, onPublish, onPlayerClick }) {
  const [lineup, setLineup] = useState(null);
  const [lineupMeta, setLineupMeta] = useState(null);
  const [activeTrainingVariantId, setActiveTrainingVariantId] = useState(null);
  const [deletedTrainingVariantIds, setDeletedTrainingVariantIds] = useState([]);
  const [savedTrainingVariants, setSavedTrainingVariants] = useState({});
  const [lineupPreferences, setLineupPreferences] = useState({ sampleCount: 0, playerPreferences: {} });
  const [activeGame, setActiveGame] = useState(0);
  const [tapSelection, setTapSelection] = useState(null);
  const [exportDone, setExportDone] = useState(false);
  const [mobileTab, setMobileTab] = useState(0);
  const [mobileMove, setMobileMove] = useState(null);
  const [autoLineupChoice, setAutoLineupChoice] = useState(null); // null | { benchPlayers: [] }
  const dragging = React.useRef(null);
  const isMobileLayout = useMediaQuery('(max-width:900px)');

  const getPlayerKey = React.useCallback(player => {
    if (!player) return null;
    return `player-${player.id}`;
  }, []);

  const isHiddenTestIdentifier = React.useCallback((identifier) => {
    if (!identifier) return false;
    const value = String(identifier).trim();
    if (HIDDEN_TEST_USER_EMAILS.has(value.toLowerCase())) return true;
    const player = players.find(p => getPlayerKey(p) === value || p.email === value);
    return !!player && HIDDEN_TEST_USER_EMAILS.has(String(player.email || '').trim().toLowerCase());
  }, [players, getPlayerKey]);

  const sanitizeLineup = React.useCallback((value) => {
    if (Array.isArray(value)) {
      return value.filter(identifier => !isHiddenTestIdentifier(identifier));
    }
    if (!value || typeof value !== 'object') return value;
    const out = {};
    Object.entries(value).forEach(([key, entry]) => {
      out[key] = sanitizeLineup(entry);
    });
    return out;
  }, [isHiddenTestIdentifier]);

  const tapMoveEnabled = canEdit && typeof window !== 'undefined' && (
    'ontouchstart' in window || (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  );

  const zoneEquals = (a, b) => {
    if (!a || !b || a.zone !== b.zone) return false;
    if (a.zone === 'bench') return true;
    return a.team === b.team && a.pos === b.pos && a.gameIdx === b.gameIdx && a.groupId === b.groupId;
  };

  const signedUpEmails = React.useMemo(() => {
    if (!event) return [];
    return (event.signups || []).filter(identifier => !isHiddenTestIdentifier(identifier));
  }, [event, isHiddenTestIdentifier]);

  const declinedEmails = React.useMemo(() => {
    if (!event) return [];
    return (event.declines || []).filter(identifier => !isHiddenTestIdentifier(identifier));
  }, [event, isHiddenTestIdentifier]);

  const signedUpPlayers = React.useMemo(() => {
    const seen = new Set();
    return signedUpEmails.map(identifier => {
      const player = players.find(p => getPlayerKey(p) === identifier || p.email === identifier) || null;
      if (!player || seen.has(player.id)) return null;
      seen.add(player.id);
      return player;
    }).filter(Boolean);
  }, [signedUpEmails, players, getPlayerKey]);

  const getPlayerObj = React.useCallback((identifier) => {
    return players.find(p => getPlayerKey(p) === identifier || p.email === identifier) || null;
  }, [players, getPlayerKey]);

  useEffect(() => {
    if (!event || !open) return;
    if (event.lineup) {
      const cloned = JSON.parse(JSON.stringify(event.lineup));
      let loaded = sanitizeLineup(cloned);
      if (loaded?.type === 'training') {
        if (!Array.isArray(loaded.variants)) {
          const legacyVariant = {
            id: 'training-1',
            name: 'Variante 1',
            teamBlue: loaded.teamBlue || { Sturm: [], Verteidigung: [], Deckel: [] },
            teamWhite: loaded.teamWhite || { Sturm: [], Verteidigung: [], Deckel: [] },
          };
          const legacyMeta = getLineupMeta(loaded);
          if (legacyMeta) Object.assign(legacyVariant, { __meta: legacyMeta });
          loaded = {
            type: 'training',
            variants: [legacyVariant],
            publishedVariantId: event.lineupPublished ? legacyVariant.id : null,
          };
        }
        const validVariants = loaded.variants.filter(variant =>
          variant && typeof variant === 'object' && !Array.isArray(variant)
        );
        loaded.variants = (validVariants.length > 0
          ? validVariants
          : [{
            id: 'training-recovered',
            teamBlue: {},
            teamWhite: {},
          }]).map((variant, index) => ({
          ...variant,
          name: `Variante ${index + 1}`,
          teamBlue: {
            Sturm: Array.isArray(variant.teamBlue?.Sturm) ? variant.teamBlue.Sturm : [],
            Verteidigung: Array.isArray(variant.teamBlue?.Verteidigung) ? variant.teamBlue.Verteidigung : [],
            Deckel: Array.isArray(variant.teamBlue?.Deckel) ? variant.teamBlue.Deckel : [],
          },
          teamWhite: {
            Sturm: Array.isArray(variant.teamWhite?.Sturm) ? variant.teamWhite.Sturm : [],
            Verteidigung: Array.isArray(variant.teamWhite?.Verteidigung) ? variant.teamWhite.Verteidigung : [],
            Deckel: Array.isArray(variant.teamWhite?.Deckel) ? variant.teamWhite.Deckel : [],
          },
        }));
        const firstVariant = loaded.variants[0];
        setActiveTrainingVariantId(firstVariant?.id || null);
        setLineupMeta(getLineupMeta(firstVariant) || getLineupMeta(cloned));
        setSavedTrainingVariants(Object.fromEntries(
          loaded.variants.map(variant => [variant.id, JSON.stringify(variant)])
        ));
      }
      if (loaded?.type === 'turnier') {
        // Legacy reserve lists are replaced by the derived reserve.
        loaded.games = (loaded.games || []).map(game => {
          const next = { ...game, swapGroups: { Sturm: [], Verteidigung: [], Deckel: [], ...(game.swapGroups || {}) } };
          delete next.reserve;
          return next;
        });
      }
      setLineup(loaded);
      if (loaded?.type === 'turnier') setLineupMeta(getLineupMeta(cloned));
    } else {
      if (event.type === 'training') {
        const variant = {
          id: newGroupId('training'),
          name: 'Variante 1',
          ...emptyTrainingLineup(),
        };
        setLineup({ type: 'training', variants: [variant], publishedVariantId: null });
        setActiveTrainingVariantId(variant.id);
        setSavedTrainingVariants({});
      } else {
        setLineup(emptyTournamentLineup());
        setActiveTrainingVariantId(null);
        setSavedTrainingVariants({});
      }
      setLineupMeta(null);
    }
    setDeletedTrainingVariantIds([]);
    setActiveGame(0);
    setTapSelection(null);
    setExportDone(false);
    setMobileTab(0);
    setMobileMove(null);
    // Do not depend on the players array here; periodic player refreshes must not wipe unsaved lineup edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.id, event?.date, event?.type]);

  useEffect(() => {
    if (!open) return undefined;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || event?.type !== 'training') return;
    setLineup(prev => {
      if (prev?.type !== 'training') return prev;
      const publishedVariantId = event.lineupPublished
        ? (event.lineup?.publishedVariantId || prev.publishedVariantId || prev.variants?.[0]?.id || null)
        : null;
      return prev.publishedVariantId === publishedVariantId
        ? prev
        : { ...prev, publishedVariantId };
    });
  }, [open, event?.type, event?.lineupPublished, event?.lineup?.publishedVariantId]);

  useEffect(() => {
    if (!open || !event?.id || !canEdit || !['training', 'turnier'].includes(event?.type)) {
      setLineupPreferences({ sampleCount: 0, playerPreferences: {} });
      return undefined;
    }

    let cancelled = false;
    const params = new URLSearchParams();
    if (event.recurring && event.date) params.set('occurrenceDate', event.date);
    const query = params.toString();
    const url = query ? `/api/events/${event.id}/lineup-preferences?${query}` : `/api/events/${event.id}/lineup-preferences`;

    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { sampleCount: 0, playerPreferences: {} })
      .then(data => {
        if (cancelled) return;
        setLineupPreferences({
          sampleCount: Number.isInteger(data?.sampleCount) ? data.sampleCount : 0,
          playerPreferences: data?.playerPreferences && typeof data.playerPreferences === 'object'
            ? data.playerPreferences
            : {},
        });
      })
      .catch(() => {
        if (!cancelled) setLineupPreferences({ sampleCount: 0, playerPreferences: {} });
      });

    return () => { cancelled = true; };
  }, [open, event?.id, event?.date, event?.recurring, event?.type, canEdit]);

  if (!event || !lineup) return null;

  const activeTrainingVariant = lineup.type === 'training'
    ? lineup.variants?.find(variant => variant.id === activeTrainingVariantId) || lineup.variants?.[0]
    : null;
  const activeTrainingLineup = activeTrainingVariant
    ? { type: 'training', ...activeTrainingVariant }
    : emptyTrainingLineup();

  const preferenceFor = (identifier) => lineupPreferences.playerPreferences?.[identifier] || null;
  const teamPreferenceCount = (identifier, teamKey) => {
    const pref = preferenceFor(identifier);
    return pref?.teams?.[teamKey] || 0;
  };
  const positionPreferenceCount = (identifier, position) => {
    const pref = preferenceFor(identifier);
    return pref?.positions?.[position] || 0;
  };
  const cappedHistoryBonus = (count, weight) => Math.min(count || 0, 3) * weight;
  const rosterHistoryBias = (rosters) => {
    return ['teamBlue', 'teamWhite'].reduce((sum, teamKey) => {
      return sum + (rosters[teamKey] || []).reduce((teamSum, player) => {
        const identifier = getPlayerKey(player);
        return teamSum + cappedHistoryBonus(teamPreferenceCount(identifier, teamKey), 0.08);
      }, 0);
    }, 0);
  };
  const isBetterTrainingCandidate = (candidate, currentBest) => {
    if (!currentBest) return true;
    if (candidate.score < currentBest.score - 0.15) return true;
    if (candidate.score > currentBest.score + 0.15) return false;
    return candidate.historyBias > currentBest.historyBias;
  };

  const markManualOverride = () => {
    const now = new Date().toISOString();
    const nextMeta = lineupMeta?.manualOverride && lineupMeta?.creationMode === 'manual'
      ? lineupMeta
      : {
        ...(lineupMeta || {}),
        creationMode: 'manual',
        manualOverride: true,
        manualUpdatedAt: now,
      };
    setLineupMeta(nextMeta);
    if (lineup.type === 'training') {
      setLineup(prev => ({
        ...prev,
        variants: prev.variants.map(variant => variant.id === activeTrainingVariantId
          ? { ...variant, __meta: getLineupMeta(withLineupMeta(variant, nextMeta)) }
          : variant),
      }));
    }
  };

  const applyAutoMeta = (usesHistory = false, historySamples = 0) => {
    const now = new Date().toISOString();
    const nextMeta = {
      ...(lineupMeta || {}),
      creationMode: 'auto',
      usesHistory,
      manualOverride: false,
      historySamples: historySamples > 0 ? historySamples : undefined,
      createdAt: lineupMeta?.createdAt || now,
      updatedAt: now,
      manualUpdatedAt: undefined,
    };
    setLineupMeta(nextMeta);
    if (lineup.type === 'training') {
      setLineup(prev => ({
        ...prev,
        variants: prev.variants.map(variant => variant.id === activeTrainingVariantId
          ? { ...variant, __meta: getLineupMeta(withLineupMeta(variant, nextMeta)) }
          : variant),
      }));
    }
  };

  const updateLineup = (updater, options = {}) => {
    if (options.manual) markManualOverride();
    setLineup(prev => {
      if (prev.type === 'training') {
        const selected = prev.variants?.find(variant => variant.id === activeTrainingVariantId) || prev.variants?.[0];
        if (!selected) return prev;
        const next = typeof updater === 'function'
          ? updater({ type: 'training', ...selected })
          : updater;
        const contents = { ...next };
        delete contents.type;
        delete contents.id;
        delete contents.name;
        return {
          ...prev,
          variants: prev.variants.map(variant => variant.id === selected.id
            ? { ...variant, ...contents }
            : variant),
        };
      }
      return typeof updater === 'function' ? updater(prev) : updater;
    });
  };

  const assignedEmails = () => {
    const assigned = new Set();
    if (lineup.type === 'training') {
      ['teamBlue', 'teamWhite'].forEach(team =>
        LINEUP_POSITIONS.forEach(pos => (activeTrainingLineup?.[team]?.[pos] || []).forEach(e => assigned.add(e)))
      );
    } else {
      const g = (lineup.games || [])[activeGame];
      if (g) LINEUP_POSITIONS.forEach(pos => (g[pos] || []).forEach(e => assigned.add(e)));
    }
    return assigned;
  };

  const getBench = () => {
    const assigned = assignedEmails();
    return signedUpEmails.filter(e => !assigned.has(e));
  };

  const appendBenchPlayersToExistingLineup = (baseLineup, benchPlayers) => {
    const nl = JSON.parse(JSON.stringify(baseLineup));
    if (!benchPlayers || benchPlayers.length === 0) return nl;

    const skillKeys = SKILL_LABELS.map(s => s.key);
    const safeSkill = (player, key) => parseInt(player?.skillStats?.[key]) || 0;
    const wavg = (sum, count) => (count > 0 ? sum / count : 0);

    // Gather existing player objects from the saved lineup
    const existingPlayers = (teamKey) =>
      LINEUP_POSITIONS.flatMap(pos => (nl[teamKey][pos] || []).map(id => getPlayerObj(id))).filter(Boolean);

    // Build additive stats object from a list of player objects
    const statsFromList = (list) => ({
      count: list.length,
      total: list.reduce((s, p) => s + skillTotal(p), 0),
      skills: skillKeys.reduce((acc, key) => { acc[key] = list.reduce((s, p) => s + safeSkill(p, key), 0); return acc; }, {}),
    });

    const addPlayerToStats = (stats, player) => ({
      count: stats.count + 1,
      total: stats.total + skillTotal(player),
      skills: skillKeys.reduce((acc, key) => { acc[key] = stats.skills[key] + safeSkill(player, key); return acc; }, {}),
    });

    // Same gap metric as autoTraining
    const evaluateGap = (b, w) => {
      const totalGap = Math.abs(wavg(b.total, b.count) - wavg(w.total, w.count));
      const skillGap = skillKeys.reduce((sum, key) =>
        sum + Math.abs(wavg(b.skills[key], b.count) - wavg(w.skills[key], w.count)), 0) / Math.max(1, skillKeys.length);
      const sizeGap = Math.abs(b.count - w.count);
      return (totalGap * 1.5) + skillGap + (sizeGap * 0.5);
    };
    const evaluateRosterOutcome = (b, w, rosters) => ({
      score: evaluateGap(b, w),
      historyBias: rosterHistoryBias(rosters),
    });

    // Sort bench players by skill desc (same as autoTraining)
    const sorted = [...benchPlayers].sort((a, b) => {
      const d = skillTotal(b) - skillTotal(a);
      return d !== 0 ? d : (a.id || 0) - (b.id || 0);
    });

    const blueBase = statsFromList(existingPlayers('teamBlue'));
    const whiteBase = statsFromList(existingPlayers('teamWhite'));

    // ── Phase 1: find the optimal split of bench players between teams ──────────
    // Exhaustive search for small groups (seeded with existing team stats so the
    // result minimises the imbalance of the FULL teams, not just the bench batch).
    let bestSplit = null;
    const exhaustiveLimit = 14;

    if (sorted.length <= exhaustiveLimit) {
      // Try floor and ceil halves separately; teams are asymmetric so both must be checked.
      const runSearch = (targetAdd) => {
        const chosen = [];
        const searchSplit = (startIdx) => {
          if (chosen.length === targetAdd) {
            const chosenSet = new Set(chosen);
            let b = blueBase;
            let w = whiteBase;
            sorted.forEach((p, i) => {
              if (chosenSet.has(i)) b = addPlayerToStats(b, p);
              else w = addPlayerToStats(w, p);
            });
            const rosters = {
              teamBlue: sorted.filter((_, i) => chosenSet.has(i)),
              teamWhite: sorted.filter((_, i) => !chosenSet.has(i)),
            };
            const candidate = { blueIndexes: new Set(chosen), ...evaluateRosterOutcome(b, w, rosters) };
            if (!bestSplit || candidate.score < bestSplit.score - 0.15 || (Math.abs(candidate.score - bestSplit.score) <= 0.15 && candidate.historyBias > bestSplit.historyBias)) {
              bestSplit = candidate;
            }
            return;
          }
          for (let i = startIdx; i < sorted.length; i++) {
            if (chosen.length + (sorted.length - i) < targetAdd) break;
            chosen.push(i);
            searchSplit(i + 1);
            chosen.pop();
          }
        };
        searchSplit(0);
      };
      runSearch(Math.floor(sorted.length / 2));
      const ceil = Math.ceil(sorted.length / 2);
      if (ceil !== Math.floor(sorted.length / 2)) runSearch(ceil);
    } else {
      // Greedy heuristic for larger groups (same logic as buildHeuristicCandidate)
      let curBlue = blueBase;
      let curWhite = whiteBase;
      const blueIdxs = new Set();
      sorted.forEach((player, idx) => {
        const withBlue = addPlayerToStats(curBlue, player);
        const withWhite = addPlayerToStats(curWhite, player);
        const existingBlue = sorted.filter((_, i) => i < idx && blueIdxs.has(i));
        const existingWhite = sorted.filter((_, i) => i < idx && !blueIdxs.has(i));
        const blueOutcome = evaluateRosterOutcome(withBlue, curWhite, {
          teamBlue: existingBlue.concat([player]),
          teamWhite: existingWhite,
        });
        const whiteOutcome = evaluateRosterOutcome(curBlue, withWhite, {
          teamBlue: existingBlue,
          teamWhite: existingWhite.concat([player]),
        });
        if (blueOutcome.score < whiteOutcome.score - 0.15 || (Math.abs(blueOutcome.score - whiteOutcome.score) <= 0.15 && blueOutcome.historyBias >= whiteOutcome.historyBias)) {
          blueIdxs.add(idx);
          curBlue = withBlue;
        } else {
          curWhite = withWhite;
        }
      });
      const rosters = {
        teamBlue: sorted.filter((_, i) => blueIdxs.has(i)),
        teamWhite: sorted.filter((_, i) => !blueIdxs.has(i)),
      };
      bestSplit = { blueIndexes: blueIdxs, ...evaluateRosterOutcome(curBlue, curWhite, rosters) };
    }

    const blueNew = sorted.filter((_, i) => bestSplit.blueIndexes.has(i));
    const whiteNew = sorted.filter((_, i) => !bestSplit.blueIndexes.has(i));

    // ── Phase 2: DP-based position assignment (mirrors placeRoster in autoTraining) ─
    const assignPositions = (teamKey, newPlayers) => {
      if (newPlayers.length === 0) return;
      const getPositionSkill = (player, pos) => parseInt(player?.skillStats?.[POSITION_SKILL_KEYS[pos]]) || 0;

      const existingCounts = LINEUP_POSITIONS.reduce((acc, pos) => {
        acc[pos] = (nl[teamKey][pos] || []).length; return acc;
      }, {});
      const totalCount = LINEUP_POSITIONS.reduce((s, pos) => s + existingCounts[pos], 0) + newPlayers.length;

      // Same slot-target formula as placeRoster
      const slotTargets = { Verteidigung: Math.min(2, totalCount), Deckel: 0, Sturm: 0 };
      if (totalCount <= 4) {
        const deckelPrim = newPlayers.filter(p => p.primaryPosition === 'Deckel').length;
        const sturmPrim  = newPlayers.filter(p => p.primaryPosition === 'Sturm').length;
        const attackPos  = deckelPrim >= sturmPrim ? 'Deckel' : 'Sturm';
        slotTargets[attackPos] = Math.max(0, totalCount - slotTargets.Verteidigung);
      } else {
        slotTargets.Deckel = Math.min(2, Math.max(0, totalCount - slotTargets.Verteidigung));
        slotTargets.Sturm  = Math.min(2, Math.max(0, totalCount - slotTargets.Verteidigung - slotTargets.Deckel));
        let extra = Math.max(0, totalCount - slotTargets.Verteidigung - slotTargets.Deckel - slotTargets.Sturm);
        const extraOrder = ['Verteidigung', 'Deckel', 'Sturm'];
        let ei = 0;
        while (extra > 0) { slotTargets[extraOrder[ei++ % extraOrder.length]] += 1; extra--; }
      }

      // Remaining open slots after accounting for already-assigned positions
      const openSlots = LINEUP_POSITIONS.flatMap(pos =>
        Array(Math.max(0, slotTargets[pos] - existingCounts[pos])).fill(pos)
      );
      // Cap to newPlayers count; any excess players fall back to least-filled position
      const slotSequence = openSlots.slice(0, newPlayers.length);

      const playerEntries = newPlayers.map((player, index) => ({ player, index, key: getPlayerKey(player) }));

      const compareForSlot = (a, b, pos) => {
        const d1 = getPositionSkill(b.player, pos) - getPositionSkill(a.player, pos); if (d1 !== 0) return d1;
        const d2 = (b.player.primaryPosition === pos) - (a.player.primaryPosition === pos); if (d2 !== 0) return d2;
        const d3 = (b.player.secondaryPosition === pos) - (a.player.secondaryPosition === pos); if (d3 !== 0) return d3;
        const d4 = skillTotal(b.player) - skillTotal(a.player); if (d4 !== 0) return d4;
        return a.index - b.index;
      };

      const memo = new Map();
      const solveSlots = (slotIdx, usedMask) => {
        const k = `${slotIdx}|${usedMask}`;
        if (memo.has(k)) return memo.get(k);
        if (slotIdx >= slotSequence.length) return { score: 0, picks: [] };
        const pos = slotSequence[slotIdx];
        const available = playerEntries.filter(e => !(usedMask & (1 << e.index)));
        const ordered = [...available].sort((a, b) => compareForSlot(a, b, pos));
        let best = null;
        ordered.forEach(entry => {
          const next = solveSlots(slotIdx + 1, usedMask | (1 << entry.index));
          const total = getPositionSkill(entry.player, pos) + next.score;
          if (!best || total > best.score) best = { score: total, picks: [entry.index, ...next.picks] };
        });
        memo.set(k, best);
        return best;
      };

      const bestAssignment = solveSlots(0, 0);
      const assignedIdxs = new Set();

      if (bestAssignment?.picks) {
        bestAssignment.picks.forEach((playerIndex, slotIdx) => {
          const pos = slotSequence[slotIdx];
          nl[teamKey][pos] = [...(nl[teamKey][pos] || []), playerEntries[playerIndex].key];
          assignedIdxs.add(playerIndex);
        });
      }

      // Overflow players (more new players than open slots) go to least-filled position
      playerEntries.forEach(entry => {
        if (!assignedIdxs.has(entry.index)) {
          const pos = [...LINEUP_POSITIONS].sort(
            (a, b) => (nl[teamKey][a] || []).length - (nl[teamKey][b] || []).length
          )[0];
          nl[teamKey][pos] = [...(nl[teamKey][pos] || []), entry.key];
        }
      });
    };

    assignPositions('teamBlue', blueNew);
    assignPositions('teamWhite', whiteNew);
    return nl;
  };

  const handleAutoTraining = () => {
    if (!lineup || lineup.type !== 'training') return;

    // Case 1: no existing assignments → create auto lineup immediately, no dialog needed
    const assigned = assignedEmails();
    if (assigned.size === 0) {
      autoTraining();
      return;
    }

    // Compute bench upfront so the dialog can show the right options
    const benchPlayers = signedUpPlayers.filter(player => {
      const key = getPlayerKey(player);
      return key && !assigned.has(key);
    });

    // Case 2 / 3: open the choice dialog
    setAutoLineupChoice({ benchPlayers });
  };

  const handleAutoLineupDialogChoice = (choice) => {
    const ctx = autoLineupChoice;
    setAutoLineupChoice(null);
    if (choice === 'new') {
      autoTraining();
    } else if (choice === 'bench' && ctx) {
      const nextLineup = appendBenchPlayersToExistingLineup(activeTrainingLineup, ctx.benchPlayers);
      applyAutoMeta(false, 0);
      updateLineup(nextLineup);
    }
    // 'keep' → do nothing
  };

  const autoTraining = () => {
    const baseSorted = [...signedUpPlayers].sort((a, b) => {
      const diff = skillTotal(b) - skillTotal(a);
      if (diff !== 0) return diff;
      return (a.id || 0) - (b.id || 0);
    });
    const skillKeys = SKILL_LABELS.map(s => s.key);
    const makeStat = () => ({
      count: 0, total: 0,
      skills: skillKeys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
    });
    const safeSkill = (player, key) => parseInt(player?.skillStats?.[key]) || 0;
    const avg = (sum, count) => (count > 0 ? sum / count : 0);
    const evaluateGap = (stats) => {
      const totalGap = Math.abs(avg(stats.teamBlue.total, stats.teamBlue.count) - avg(stats.teamWhite.total, stats.teamWhite.count));
      const skillGap = skillKeys.reduce((sum, key) => {
        return sum + Math.abs(avg(stats.teamBlue.skills[key], stats.teamBlue.count) - avg(stats.teamWhite.skills[key], stats.teamWhite.count));
      }, 0) / Math.max(1, skillKeys.length);
      const sizeGap = Math.abs(stats.teamBlue.count - stats.teamWhite.count);
      return (totalGap * 1.5) + skillGap + (sizeGap * 0.5);
    };
    const evaluateFromRosters = (rosters) => {
      const stats = { teamBlue: makeStat(), teamWhite: makeStat() };
      ['teamBlue', 'teamWhite'].forEach(teamKey => {
        rosters[teamKey].forEach(player => {
          stats[teamKey].count += 1;
          stats[teamKey].total += skillTotal(player);
          skillKeys.forEach(key => { stats[teamKey].skills[key] += safeSkill(player, key); });
        });
      });
      return {
        score: evaluateGap(stats),
        historyBias: rosterHistoryBias(rosters),
      };
    };
    const buildHeuristicCandidate = (ordered, variantIndex) => {
      const stats = { teamBlue: makeStat(), teamWhite: makeStat() };
      const rosters = { teamBlue: [], teamWhite: [] };
      const imbalanceCost = (teamKey, player) => {
        const otherKey = teamKey === 'teamBlue' ? 'teamWhite' : 'teamBlue';
        const current = stats[teamKey];
        const other = stats[otherKey];
        const nextCount = current.count + 1;
        const nextTotal = current.total + skillTotal(player);
        const totalGap = Math.abs(avg(nextTotal, nextCount) - avg(other.total, other.count));
        const skillGap = skillKeys.reduce((sum, key) => {
          const nextSkillAvg = avg(current.skills[key] + safeSkill(player, key), nextCount);
          const otherSkillAvg = avg(other.skills[key], other.count);
          return sum + Math.abs(nextSkillAvg - otherSkillAvg);
        }, 0) / Math.max(1, skillKeys.length);
        const sizeGap = Math.abs(nextCount - other.count);
        return (totalGap * 1.35) + skillGap + (sizeGap * 0.4);
      };
      ordered.forEach((player, idx) => {
        const blueCost = imbalanceCost('teamBlue', player);
        const whiteCost = imbalanceCost('teamWhite', player);
        let team = blueCost <= whiteCost ? 'teamBlue' : 'teamWhite';
        if (Math.abs(blueCost - whiteCost) < 0.01) {
          if (stats.teamBlue.count !== stats.teamWhite.count) {
            team = stats.teamBlue.count < stats.teamWhite.count ? 'teamBlue' : 'teamWhite';
          } else if (stats.teamBlue.total !== stats.teamWhite.total) {
            team = stats.teamBlue.total <= stats.teamWhite.total ? 'teamBlue' : 'teamWhite';
          } else {
            team = ((idx + variantIndex) % 2 === 0) ? 'teamBlue' : 'teamWhite';
          }
        }
        rosters[team].push(player);
        stats[team].count += 1;
        stats[team].total += skillTotal(player);
        skillKeys.forEach(key => { stats[team].skills[key] += safeSkill(player, key); });
      });
      return { rosters, score: evaluateGap(stats), historyBias: rosterHistoryBias(rosters) };
    };
    const exhaustiveLimit = 14;
    let bestCandidate = null;
    if (baseSorted.length > 1 && baseSorted.length <= exhaustiveLimit) {
      const targetBlueCount = Math.floor(baseSorted.length / 2);
      const chosenIndexes = [];
      const search = (startIdx) => {
        if (chosenIndexes.length === targetBlueCount) {
          if (!chosenIndexes.includes(0)) return;
          const chosen = new Set(chosenIndexes);
          const rosters = {
            teamBlue: baseSorted.filter((_, i) => chosen.has(i)),
            teamWhite: baseSorted.filter((_, i) => !chosen.has(i)),
          };
          const outcome = evaluateFromRosters(rosters);
          if (isBetterTrainingCandidate({ rosters, ...outcome }, bestCandidate)) bestCandidate = { rosters, ...outcome };
          return;
        }
        for (let i = startIdx; i < baseSorted.length; i++) {
          if (chosenIndexes.length + (baseSorted.length - i) < targetBlueCount) break;
          chosenIndexes.push(i);
          search(i + 1);
          chosenIndexes.pop();
        }
      };
      search(0);
    }
    if (!bestCandidate) {
      const candidateOrders = [
        baseSorted,
        [...baseSorted].reverse(),
        [...baseSorted].filter((_, i) => i % 2 === 0).concat([...baseSorted].filter((_, i) => i % 2 === 1)),
        [...baseSorted].sort((a, b) => {
          const pa = LINEUP_POSITIONS.includes(a.primaryPosition) ? LINEUP_POSITIONS.indexOf(a.primaryPosition) : 99;
          const pb = LINEUP_POSITIONS.includes(b.primaryPosition) ? LINEUP_POSITIONS.indexOf(b.primaryPosition) : 99;
          if (pa !== pb) return pa - pb;
          return skillTotal(b) - skillTotal(a);
        }),
        [...baseSorted].sort((a, b) => {
          const sa = LINEUP_POSITIONS.includes(a.secondaryPosition) ? LINEUP_POSITIONS.indexOf(a.secondaryPosition) : 99;
          const sb = LINEUP_POSITIONS.includes(b.secondaryPosition) ? LINEUP_POSITIONS.indexOf(b.secondaryPosition) : 99;
          if (sa !== sb) return sa - sb;
          return skillTotal(b) - skillTotal(a);
        }),
      ];
      bestCandidate = candidateOrders
        .map((ordered, idx) => buildHeuristicCandidate(ordered, idx))
        .sort((a, b) => {
          if (Math.abs(a.score - b.score) > 0.15) return a.score - b.score;
          return b.historyBias - a.historyBias;
        })[0];
    }
    const rosters = bestCandidate?.rosters || { teamBlue: [], teamWhite: [] };
    const nl = emptyTrainingLineup();
    const placeRoster = (teamKey) => {
      const teamPlayers = rosters[teamKey];
      const totalPlayers = teamPlayers.length;
      const getPositionSkill = (player, pos) => parseInt(player?.skillStats?.[POSITION_SKILL_KEYS[pos]]) || 0;

      const chooseSmallTeamAttackPosition = () => {
        const deckelPrimary = teamPlayers.filter(player => player.primaryPosition === 'Deckel');
        const sturmPrimary = teamPlayers.filter(player => player.primaryPosition === 'Sturm');
        if (deckelPrimary.length !== sturmPrimary.length) {
          return deckelPrimary.length > sturmPrimary.length ? 'Deckel' : 'Sturm';
        }
        const deckelSkill = deckelPrimary.reduce((sum, player) => sum + getPositionSkill(player, 'Deckel'), 0);
        const sturmSkill = sturmPrimary.reduce((sum, player) => sum + getPositionSkill(player, 'Sturm'), 0);
        if (deckelSkill !== sturmSkill) {
          return deckelSkill >= sturmSkill ? 'Deckel' : 'Sturm';
        }
        return 'Deckel';
      };

      const slotTargets = {
        Verteidigung: Math.min(2, totalPlayers),
        Deckel: 0,
        Sturm: 0,
      };

      if (totalPlayers <= 4) {
        const attackPosition = chooseSmallTeamAttackPosition();
        slotTargets[attackPosition] = Math.max(0, totalPlayers - slotTargets.Verteidigung);
      } else {
        slotTargets.Deckel = Math.min(2, Math.max(0, totalPlayers - slotTargets.Verteidigung));
        slotTargets.Sturm = Math.min(2, Math.max(0, totalPlayers - slotTargets.Verteidigung - slotTargets.Deckel));
        let extraSlots = Math.max(0, totalPlayers - slotTargets.Verteidigung - slotTargets.Deckel - slotTargets.Sturm);
        const extraOrder = ['Verteidigung', 'Deckel', 'Sturm'];
        let extraIndex = 0;
        while (extraSlots > 0) {
          slotTargets[extraOrder[extraIndex % extraOrder.length]] += 1;
          extraSlots -= 1;
          extraIndex += 1;
        }
      }

      const slotSequence = [];
      LINEUP_POSITIONS.forEach(pos => {
        for (let i = 0; i < (slotTargets[pos] || 0); i += 1) {
          slotSequence.push(pos);
        }
      });

      const playerEntries = teamPlayers.map((player, index) => ({ player, index, key: getPlayerKey(player) }));
      const compareForSlot = (a, b, pos) => {
        const statDiff = getPositionSkill(b.player, pos) - getPositionSkill(a.player, pos);
        if (statDiff !== 0) return statDiff;
        const prefDiff = positionPreferenceCount(b.key, pos) - positionPreferenceCount(a.key, pos);
        if (prefDiff !== 0) return prefDiff;
        const primaryDiff = (b.player.primaryPosition === pos) - (a.player.primaryPosition === pos);
        if (primaryDiff !== 0) return primaryDiff;
        const secondaryDiff = (b.player.secondaryPosition === pos) - (a.player.secondaryPosition === pos);
        if (secondaryDiff !== 0) return secondaryDiff;
        const totalDiff = skillTotal(b.player) - skillTotal(a.player);
        if (totalDiff !== 0) return totalDiff;
        return a.index - b.index;
      };

      const memo = new Map();
      const solveSlots = (slotIdx, usedMask) => {
        const memoKey = `${slotIdx}|${usedMask}`;
        if (memo.has(memoKey)) return memo.get(memoKey);
        if (slotIdx >= slotSequence.length) return { score: 0, picks: [] };

        const pos = slotSequence[slotIdx];
        const available = playerEntries.filter(entry => !(usedMask & (1 << entry.index)));
        const ordered = [...available].sort((a, b) => compareForSlot(a, b, pos));

        let best = null;
        ordered.forEach(entry => {
          const next = solveSlots(slotIdx + 1, usedMask | (1 << entry.index));
          const total = getPositionSkill(entry.player, pos)
            + cappedHistoryBonus(positionPreferenceCount(entry.key, pos), 0.35)
            + cappedHistoryBonus(teamPreferenceCount(entry.key, teamKey), 0.15)
            + next.score;
          if (!best || total > best.score) {
            best = { score: total, picks: [entry.index, ...next.picks] };
          }
        });

        memo.set(memoKey, best);
        return best;
      };

      const bestAssignment = solveSlots(0, 0);
      if (bestAssignment?.picks?.length === slotSequence.length) {
        bestAssignment.picks.forEach((playerIndex, slotIdx) => {
          const pos = slotSequence[slotIdx];
          const playerKey = playerEntries[playerIndex].key;
          nl[teamKey][pos] = [...(nl[teamKey][pos] || []), playerKey];
        });
      }
    };
    placeRoster('teamBlue');
    placeRoster('teamWhite');
    applyAutoMeta(lineupPreferences.sampleCount > 0, lineupPreferences.sampleCount);
    updateLineup(nl);
  };

  const autoTournament = () => {
    if ((lineup.games || []).length === 0) return;
    const nl = { ...lineup, games: lineup.games.map((g, i) => i === activeGame ? emptyGame(activeGame) : { ...g }) };
    const g = nl.games[activeGame];
    const used = new Set();
    const preferredPlayersFor = (position, mode) => {
      return [...signedUpPlayers]
        .filter(player => !used.has(getPlayerKey(player)))
        .sort((a, b) => {
          const aKey = getPlayerKey(a);
          const bKey = getPlayerKey(b);
          const prefDiff = positionPreferenceCount(bKey, position) - positionPreferenceCount(aKey, position);
          if (prefDiff !== 0) return prefDiff;
          if (mode === 'primary') {
            const primaryDiff = (b.primaryPosition === position) - (a.primaryPosition === position);
            if (primaryDiff !== 0) return primaryDiff;
          }
          if (mode === 'secondary') {
            const secondaryDiff = (b.secondaryPosition === position) - (a.secondaryPosition === position);
            if (secondaryDiff !== 0) return secondaryDiff;
          }
          return skillTotal(b) - skillTotal(a);
        });
    };
    LINEUP_POSITIONS.forEach(pos => {
      preferredPlayersFor(pos, 'primary')
        .filter(p => p.primaryPosition === pos)
        .slice(0, TOURNAMENT_MAX_PER_POS)
        .forEach(p => { const playerKey = getPlayerKey(p); g[pos].push(playerKey); used.add(playerKey); });
    });
    LINEUP_POSITIONS.forEach(pos => {
      if (g[pos].length < TOURNAMENT_MAX_PER_POS) {
        preferredPlayersFor(pos, 'secondary')
          .filter(p => p.secondaryPosition === pos)
          .slice(0, TOURNAMENT_MAX_PER_POS - g[pos].length)
          .forEach(p => { const playerKey = getPlayerKey(p); g[pos].push(playerKey); used.add(playerKey); });
      }
    });
    signedUpPlayers.filter(p => !used.has(getPlayerKey(p))).forEach(p => {
      const playerKey = getPlayerKey(p);
      const target = LINEUP_POSITIONS
        .filter(pos => g[pos].length < TOURNAMENT_MAX_PER_POS)
        .sort((a, b) => {
          const prefDiff = positionPreferenceCount(playerKey, b) - positionPreferenceCount(playerKey, a);
          if (prefDiff !== 0) return prefDiff;
          return g[a].length - g[b].length;
        })[0];
      if (target) {
        g[target].push(playerKey);
        used.add(playerKey);
      }
    });
    applyAutoMeta(lineupPreferences.sampleCount > 0, lineupPreferences.sampleCount);
    updateLineup(nl);
  };

  const removeFromGame = (g, pos, email) => {
    g[pos] = (g[pos] || []).filter(e => e !== email);
    if (g.swapGroups?.[pos]) {
      g.swapGroups[pos] = g.swapGroups[pos].map(group => ({ ...group, players: group.players.filter(e => e !== email) }));
    }
  };

  const movePlayer = (email, from, to) => {
    updateLineup(prev => {
      const nl = JSON.parse(JSON.stringify(prev));
      if (nl.type === 'turnier' && to.zone !== 'bench') {
        const g = nl.games[to.gameIdx];
        g.swapGroups = { Sturm: [], Verteidigung: [], Deckel: [], ...(g.swapGroups || {}) };
        // Capacity is checked against the target state without the moved player.
        const without = (g[to.pos] || []).filter(e => e !== email);
        if (without.length >= TOURNAMENT_MAX_PER_POS) return prev;
        if (to.groupId) {
          const group = g.swapGroups[to.pos].find(gr => gr.id === to.groupId);
          if (!group) return prev;
          const others = group.players.filter(e => e !== email);
          if (others.length >= SWAP_MODES[group.mode].size) return prev;
        }
      }
      if (from.zone !== 'bench') {
        if (nl.type === 'training') {
          nl[from.team][from.pos] = nl[from.team][from.pos].filter(e => e !== email);
        } else {
          removeFromGame(nl.games[from.gameIdx], from.pos, email);
        }
      }
      if (to.zone !== 'bench') {
        if (nl.type === 'training') {
          nl[to.team][to.pos] = [...(nl[to.team][to.pos] || []), email];
        } else {
          const g = nl.games[to.gameIdx];
          g[to.pos] = [...(g[to.pos] || []), email];
          if (to.groupId) {
            const group = g.swapGroups[to.pos].find(gr => gr.id === to.groupId);
            group.players.push(email);
          }
        }
      }
      return nl;
    }, { manual: true });
  };

  const updateGroups = (pos, mutate) => {
    updateLineup(prev => {
      const nl = JSON.parse(JSON.stringify(prev));
      const g = nl.games[activeGame];
      g.swapGroups = { Sturm: [], Verteidigung: [], Deckel: [], ...(g.swapGroups || {}) };
      g.swapGroups[pos] = mutate(g.swapGroups[pos] || []);
      return nl;
    }, { manual: true });
  };

  const addGroup = (pos) => updateGroups(pos, groups => [...groups, { id: newGroupId(pos), mode: '1:1', players: [] }]);

  const removeGroup = (pos, groupId) => updateGroups(pos, groups => groups.filter(gr => gr.id !== groupId));

  // Players beyond the new mode size become ungrouped (they stay in the position).
  const setGroupMode = (pos, groupId, mode) => updateGroups(pos, groups => groups.map(gr => (
    gr.id === groupId ? { ...gr, mode, players: gr.players.slice(0, SWAP_MODES[mode].size) } : gr
  )));

  // Swapping array positions swaps the fixed roles (Wasser <-> Wechselbank).
  const swapGroupPlayers = (pos, groupId, i, j) => updateGroups(pos, groups => groups.map(gr => {
    if (gr.id !== groupId) return gr;
    const players = [...gr.players];
    [players[i], players[j]] = [players[j], players[i]];
    return { ...gr, players };
  }));

  const teamAvgSkill = (emailList) => {
    if (!emailList.length) return 0;
    return Math.round(emailList.reduce((s, email) => {
      const p = getPlayerObj(email);
      return s + skillTotal(p);
    }, 0) / emailList.length);
  };

  const weightedPlayerStrength = (player, position) => {
    if (!player) return 0;
    const baseTotal = skillTotal(player);
    const skillKey = POSITION_SKILL_KEYS[position];
    if (!skillKey) return baseTotal;
    const positionSkill = parseInt(player.skillStats?.[skillKey]) || 0;
    const fit = getPosFit(player, position);
    const bonus = POSITION_WEIGHT_BONUS[fit] || 0;
    return baseTotal + (positionSkill * bonus);
  };

  const mobileDestinationsFor = (fromZone) => {
    if (lineup?.type === 'training') {
      const zones = ['teamBlue', 'teamWhite'].flatMap(team =>
        LINEUP_POSITIONS.map(pos => ({
          label: `${team === 'teamBlue' ? 'Blau' : 'Weiß'} · ${pos}`,
          to: { zone: 'pos', team, pos },
          colorSx: team === 'teamBlue'
            ? { bgcolor: '#E3F2FD', color: '#0D47A1', borderColor: '#90CAF9' }
            : { bgcolor: '#ECEFF1', color: '#455A64', borderColor: '#B0BEC5' },
        }))
      );
      zones.push({ label: 'Bank', to: { zone: 'bench' }, colorSx: { bgcolor: '#F5F5F5', color: '#616161', borderColor: '#E0E0E0' } });
      return zones.filter(({ to }) => {
        if (fromZone.zone === 'bench') return to.zone !== 'bench';
        if (to.zone === 'bench') return true;
        return !(fromZone.team === to.team && fromZone.pos === to.pos);
      });
    }
    const game = lineup?.games?.[activeGame];
    // Only offer targets that still have a free slot (a position's own list does not count against itself).
    const hasRoomFor = (to) => {
      const samePos = fromZone.zone === 'pos' && fromZone.gameIdx === to.gameIdx && fromZone.pos === to.pos;
      if (!samePos && (game?.[to.pos] || []).length >= TOURNAMENT_MAX_PER_POS) return false;
      if (!to.groupId) return true;
      const group = getSwapGroups(game, to.pos).find(gr => gr.id === to.groupId);
      return !!group && group.players.length < SWAP_MODES[group.mode].size;
    };
    const zones = [
      ...LINEUP_POSITIONS.flatMap(pos => [
        { label: pos, to: { zone: 'pos', pos, gameIdx: activeGame }, colorSx: { bgcolor: '#E3F2FD', color: '#0D47A1', borderColor: '#90CAF9' } },
        ...getSwapGroups(game, pos).map((group, idx) => ({
          label: `${pos} · ${groupLetter(idx)}`,
          to: { zone: 'pos', pos, gameIdx: activeGame, groupId: group.id },
          colorSx: { bgcolor: '#fff', color: groupColor(idx), borderColor: groupColor(idx) },
        })),
      ]),
      { label: 'Reserve', to: { zone: 'bench' }, colorSx: { bgcolor: '#F3E5F5', color: '#6A1B9A', borderColor: '#CE93D8' } },
    ];
    return zones.filter(({ to }) => {
      if (fromZone.zone === 'bench') return to.zone !== 'bench' && hasRoomFor(to);
      if (to.zone === 'bench') return true;
      return !zoneEquals(fromZone, to) && hasRoomFor(to);
    });
  };

  const SwapButton = ({ email, fromZone }) => {
    if (!canEdit || !fromZone?.groupId || !lineup?.games) return null;
    const group = getSwapGroups(lineup.games[fromZone.gameIdx], fromZone.pos).find(gr => gr.id === fromZone.groupId);
    const index = group ? group.players.indexOf(email) : -1;
    const partner = index < 0 ? null : getSwapPartnerIndex(group.mode, group.players.length, index);
    if (partner === null) return null;
    return (
      <IconButton size="small" title="Wasser/Wechselbank tauschen" sx={{ p: 0.4, color: '#455A64' }}
        onClick={(e) => { e.stopPropagation(); swapGroupPlayers(fromZone.pos, group.id, index, partner); }}>
        <SwapVertIcon sx={{ fontSize: 16 }} />
      </IconButton>
    );
  };

  const getMark = (zone) => {
    if (!zone?.groupId || !lineup?.games) return null;
    const groups = getSwapGroups(lineup.games[zone.gameIdx], zone.pos);
    const idx = groups.findIndex(group => group.id === zone.groupId);
    if (idx < 0) return null;
    return { letter: groupLetter(idx), color: groupColor(idx), mode: groups[idx].mode };
  };

  const MarkBadge = ({ mark }) => (mark ? (
    <Box title={SWAP_MODES[mark.mode].label} sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      minWidth: 16, height: 16, px: 0.4, borderRadius: '8px', bgcolor: mark.color, color: '#fff', fontSize: 10, fontWeight: 800,
    }}>
      {mark.mode === 'solo' ? 'D' : mark.letter}
    </Box>
  ) : null);

  const MobilePlayerRow = ({ email, fromZone }) => {
    const p = getPlayerObj(email);
    const isSelected = mobileMove && mobileMove.email === email && zoneEquals(mobileMove.from, fromZone);
    const display = p ? `${p.firstName} ${p.lastName}` : email;
    const inPosition = fromZone?.zone === 'pos' && fromZone?.pos;
    const isPrimary = inPosition && p?.primaryPosition === fromZone.pos;
    const isSecondary = inPosition && p?.secondaryPosition === fromZone.pos;
    const marker = isPrimary ? 'primary' : (isSecondary ? 'secondary' : null);
    const mark = getMark(fromZone);
    return (
      <Box
        onClick={() => setMobileMove(isSelected ? null : { email, from: fromZone })}
        sx={{ border: `1px solid ${mark ? mark.color : '#e0e7ef'}`, borderLeft: mark ? `4px solid ${mark.color}` : undefined, borderRadius: 2, p: 0.6, bgcolor: isSelected ? '#E3F2FD' : '#fff', cursor: 'pointer' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: '#0D47A1' }}>
            {p?.capNumber || '?'}
          </Avatar>
          <MarkBadge mark={mark} />
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {display}
          </Typography>
          {marker === 'primary' && (
            <Box title="Primärposition" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', bgcolor: '#E8F5E9' }}>
              <LooksOneIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
            </Box>
          )}
          {marker === 'secondary' && (
            <Box title="Sekundärposition" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', bgcolor: '#FFF3E0' }}>
              <LooksTwoIcon sx={{ fontSize: 14, color: '#E65100' }} />
            </Box>
          )}
          <SwapButton email={email} fromZone={fromZone} />
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); p && onPlayerClick && onPlayerClick(p); }} sx={{ color: '#0D47A1' }}>
            <PersonIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        {isSelected && (
          <Box sx={{ mt: 0.6, display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
            {mobileDestinationsFor(fromZone).map(dest => (
              <Chip
                key={dest.label}
                label={dest.label}
                size="small"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  movePlayer(email, fromZone, dest.to);
                  setMobileMove(null);
                }}
                sx={{ height: 20, fontSize: 10, ...dest.colorSx }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const PlayerCard = ({ email, fromZone, inPos }) => {
    const p = getPlayerObj(email);
    const fit = inPos ? getPosFit(p, inPos) : 'none';
    const fitColor = fit === 'primary' ? '#2E7D32' : fit === 'secondary' ? '#E65100' : '#9E9E9E';
    const name = p ? `${p.firstName} ${p.lastName}` : email;
    const cap = p?.capNumber ? `${p.capNumber}` : '?';
    const declined = declinedEmails.includes(email);
    const isSelected = !!tapSelection && tapSelection.email === email && zoneEquals(tapSelection.from, fromZone);
    const favoritePositions = [p?.primaryPosition, p?.secondaryPosition].filter(Boolean);
    const mark = getMark(fromZone);
    return (
      <Box
        draggable={canEdit}
        onDragStart={canEdit ? () => { dragging.current = { email, from: fromZone }; } : undefined}
        onClick={() => {
          if (tapMoveEnabled) {
            if (isSelected) { setTapSelection(null); return; }
            setTapSelection({ email, from: fromZone });
            return;
          }
          if (p && onPlayerClick) onPlayerClick(p);
        }}
        sx={{
          display: 'flex', alignItems: 'center',
          width: '100%', minHeight: 38, bgcolor: declined ? '#FFF3E0' : '#fff', borderRadius: 2,
          border: `1px solid ${declined ? '#FF8F00' : (mark ? mark.color : '#dce6f2')}`, cursor: canEdit ? 'grab' : 'default',
          px: 0.75, py: 0.45, gap: 0.7, position: 'relative',
          borderLeft: `4px solid ${fitColor}`,
          boxShadow: isSelected ? '0 0 0 2px #1976d2 inset' : 'none',
          '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderColor: '#90CAF9' },
        }}>
        {canEdit && <DragIndicatorIcon sx={{ fontSize: 14, color: '#9fb4ce', flexShrink: 0 }} />}
        <Avatar sx={{ width: 24, height: 24, fontSize: 10, fontWeight: 700, bgcolor: '#0D47A1', flexShrink: 0 }}>
          {cap}
        </Avatar>
        <MarkBadge mark={mark} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, textDecoration: declined ? 'line-through' : 'none', color: declined ? '#BF360C' : 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.35 }}>
            {favoritePositions.length > 0 ? favoritePositions.map((position, index) => {
              const matchesAssigned = !!inPos && position === inPos;
              return (
                <Chip key={`${position}-${index}`} label={position} size="small"
                  sx={{ height: 18, fontSize: 9, fontWeight: matchesAssigned ? 700 : 600,
                    bgcolor: matchesAssigned ? `${fitColor}16` : '#EEF3F8',
                    color: matchesAssigned ? fitColor : '#607D8B',
                    border: `1px solid ${matchesAssigned ? fitColor : '#dce6f2'}`,
                    '& .MuiChip-label': { px: 0.6 },
                  }} />
              );
            }) : (
              <Typography variant="caption" sx={{ fontSize: 10, color: '#607D8B', lineHeight: 1.1 }}>Keine Position</Typography>
            )}
          </Box>
        </Box>
        <SwapButton email={email} fromZone={fromZone} />
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); p && onPlayerClick && onPlayerClick(p); }} sx={{ color: '#0D47A1', p: 0.4 }}>
          <PersonIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {declined && (
          <Chip label="Abgesagt" size="small" sx={{
            position: 'absolute', top: -7, right: 22,
            height: 14, fontSize: 8, fontWeight: 700, bgcolor: '#FF8F00', color: '#fff',
            '& .MuiChip-label': { px: 0.55 },
          }} />
        )}
      </Box>
    );
  };

  const DropZone = ({ label, emails, toZone, maxSlots, accentColor, posForFit, slotLabels, slotMode, headerExtra }) => {
    const [over, setOver] = React.useState(false);
    const slots = maxSlots || 99;
    const fitPos = posForFit !== undefined ? posForFit : (label !== 'Reserve' ? label : null);
    return (
      <Box
        onDragOver={canEdit ? (e) => { e.preventDefault(); setOver(true); } : undefined}
        onDragLeave={canEdit ? () => setOver(false) : undefined}
        onDrop={canEdit ? (e) => {
          e.preventDefault(); setOver(false);
          if (dragging.current) {
            movePlayer(dragging.current.email, dragging.current.from, toZone);
            dragging.current = null;
          }
        } : undefined}
        onClick={tapMoveEnabled ? () => {
          if (!tapSelection) return;
          if (zoneEquals(tapSelection.from, toZone)) { setTapSelection(null); return; }
          movePlayer(tapSelection.email, tapSelection.from, toZone);
          setTapSelection(null);
        } : undefined}
        sx={{
          border: `2px dashed ${over ? (accentColor || '#0D47A1') : '#e0e7ef'}`,
          borderRadius: 2, p: 0.85, minHeight: 96,
          bgcolor: over ? `${accentColor || '#0D47A1'}08` : 'transparent',
          transition: 'all 0.15s',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color={accentColor || 'text.secondary'}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{label}</Typography>
          <Chip label={emails.length} size="small"
            sx={{ height: 14, fontSize: 9, bgcolor: accentColor ? `${accentColor}20` : '#e0e7ef',
              color: accentColor || 'text.secondary', fontWeight: 700, '& .MuiChip-label': { px: 0.5 } }} />
          {headerExtra}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45 }}>
          {emails.map((email, i) => (
            <Box key={email}>
              {slotLabels && slotCaption(slotMode, i) && (
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', lineHeight: 1.3 }}>{slotCaption(slotMode, i)}</Typography>
              )}
              <PlayerCard email={email} fromZone={toZone} inPos={fitPos} />
            </Box>
          ))}
          {maxSlots && Array.from({ length: Math.max(0, slots - emails.length) }).map((_, i) => (
            <Box key={`empty-${i}`} sx={{
              width: '100%', minHeight: 32, borderRadius: 1.5, border: '1px dashed #d7e3f4',
              bgcolor: '#f8fbff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: 10, color: '#b0bec5' }}>{(slotMode && slotCaption(slotMode, emails.length + i)) || 'Freier Platz'}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const TeamStats = ({ allEmails, accentColor }) => {
    const avg = teamAvgSkill(allEmails);
    const teamPlayers = allEmails.map(getPlayerObj).filter(Boolean);
    const skillAvgs = SKILL_LABELS.map(({ key, label }) => {
      const vals = teamPlayers.map(p => parseInt(p.skillStats?.[key])).filter(v => !isNaN(v));
      return { label, avg: vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null };
    }).filter(s => s.avg !== null);
    return (
      <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${accentColor}20` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
          <Chip label={`Ø Gesamt: ${avg}`} size="small"
            sx={{ height: 20, fontSize: 11, bgcolor: `${accentColor}15`, color: accentColor, fontWeight: 700 }} />
          <Chip label={`${allEmails.length} Spieler`} size="small"
            sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />
        </Box>
        {skillAvgs.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px' }}>
            {skillAvgs.map(({ label, avg: a }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: '1px' }}>
                <Typography sx={{ fontSize: 10, color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
                <Box sx={{ width: 32, height: 4, bgcolor: '#e0e7ef', borderRadius: 2, flexShrink: 0 }}>
                  <Box sx={{ height: '100%', width: `${(a / 10) * 100}%`,
                    bgcolor: a >= 7 ? '#2E7D32' : a >= 4 ? accentColor : '#9E9E9E', borderRadius: 2 }} />
                </Box>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: accentColor, minWidth: 14, textAlign: 'right', lineHeight: 1 }}>{a}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Keine Skill-Daten eingetragen</Typography>
        )}
      </Box>
    );
  };

  const TeamStatsComparison = ({ bluePositions, whitePositions }) => {
    const buildStats = (positions) => {
      const teamPlayers = LINEUP_POSITIONS.flatMap(pos => (positions?.[pos] || []).map(email => ({
        player: getPlayerObj(email),
        position: pos,
      }))).filter(({ player }) => !!player);
      const weightedSumRaw = teamPlayers.reduce((sum, { player, position }) => sum + weightedPlayerStrength(player, position), 0);
      const weightedSum = teamPlayers.length ? Math.round(weightedSumRaw) : null;
      const weightedAvg = teamPlayers.length ? Math.round(weightedSumRaw / teamPlayers.length) : null;
      const skills = SKILL_LABELS.reduce((acc, { key }) => {
        const vals = teamPlayers.map(({ player }) => parseInt(player.skillStats?.[key])).filter(v => !isNaN(v));
        acc[key] = vals.length ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length) : null;
        return acc;
      }, {});
      return { count: teamPlayers.length, weightedSum, weightedAvg, skills };
    };
    const blue = buildStats(bluePositions);
    const white = buildStats(whitePositions);
    const rows = [
      { key: '__count__', label: 'Spieler', blue: blue.count, white: white.count },
      { key: '__strength__', label: 'Gesamtstärke', blue: blue.weightedSum, white: white.weightedSum },
      { key: '__quality__', label: 'Ø Qualität', blue: blue.weightedAvg, white: white.weightedAvg },
      ...SKILL_LABELS.map(({ key, label }) => ({ label, key, blue: blue.skills[key], white: white.skills[key] })),
    ];
    const showVal = (v) => (v == null ? '–' : v);
    return (
      <Box sx={{ width: '100%', border: '1px solid #d7e3f4', borderRadius: 2, p: 1, overflowX: 'auto' }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, color: 'text.secondary', fontWeight: 700, letterSpacing: 0.4 }}>
          Teamvergleich (Training)
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(92px,1fr) 56px 56px 52px', sm: 'minmax(120px,1.2fr) 1fr 1fr auto' }, gap: 0.75, alignItems: 'center', width: '100%', minWidth: { xs: 250, sm: 0 } }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Kategorie</Typography>
          <Chip label="Team Blau" size="small" sx={{ height: 20, bgcolor: '#0D47A115', color: TEAM_COLORS.blue, fontWeight: 700 }} />
          <Chip label="Team Weiß" size="small" sx={{ height: 20, bgcolor: '#78909C20', color: TEAM_COLORS.white, fontWeight: 700 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Vorteil</Typography>
          {rows.map(row => {
            const signedDelta = row.blue != null && row.white != null ? row.blue - row.white : null;
            const absDelta = signedDelta == null ? null : Math.abs(signedDelta);
            const advLabel = signedDelta == null ? '–' : signedDelta === 0 ? 'Gleich' : signedDelta > 0 ? `Blau +${absDelta}` : `Weiß +${absDelta}`;
            const advColor = signedDelta == null ? 'text.disabled' : signedDelta === 0 ? 'text.secondary' : signedDelta > 0 ? TEAM_COLORS.blue : TEAM_COLORS.white;
            return (
              <React.Fragment key={row.key}>
                <Typography sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: ['__strength__', '__quality__', '__count__'].includes(row.key) ? 700 : 500, color: ['__strength__', '__quality__', '__count__'].includes(row.key) ? 'text.primary' : 'text.secondary' }}>{row.label}</Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 700, color: TEAM_COLORS.blue, textAlign: 'center' }}>{showVal(row.blue)}</Typography>
                <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 700, color: TEAM_COLORS.white, textAlign: 'center' }}>{showVal(row.white)}</Typography>
                <Typography sx={{ fontSize: { xs: 9, sm: 11 }, fontWeight: 700, textAlign: 'right', color: advColor, whiteSpace: 'nowrap' }}>{advLabel}</Typography>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  };

  const bench = getBench();
  const benchZone = { zone: 'bench' };
  const BenchPanel = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'auto', md: '100%' } }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10, mb: 0.75, display: 'block' }}>
        {lineup.type === 'turnier' ? 'Reserve' : 'Bank'} ({bench.length})
      </Typography>
      <Box
        onDragOver={canEdit ? (e) => e.preventDefault() : undefined}
        onDrop={canEdit ? (e) => {
          e.preventDefault();
          if (dragging.current) { movePlayer(dragging.current.email, dragging.current.from, benchZone); dragging.current = null; }
        } : undefined}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.55, minHeight: 80, border: '2px dashed #e0e7ef', borderRadius: 2, p: 0.85, bgcolor: '#f8fbff', overflowY: 'auto' }}
        onClick={tapMoveEnabled ? () => {
          if (!tapSelection) return;
          if (tapSelection.from.zone === 'bench') { setTapSelection(null); return; }
          movePlayer(tapSelection.email, tapSelection.from, benchZone);
          setTapSelection(null);
        } : undefined}>
        {bench.length === 0
          ? <Typography variant="caption" color="text.disabled" sx={{ m: 'auto', textAlign: 'center' }}>Alle Spieler eingeteilt</Typography>
          : bench.map(email => <PlayerCard key={email} email={email} fromZone={benchZone} inPos={null} />)}
      </Box>
    </Box>
  );

  const isTurnier = lineup.type === 'turnier';
  const canPublishActiveTrainingVariant = isTurnier || (
    event.lineup?.type === 'training' && activeTrainingVariant && (
      event.lineup.variants?.some(variant => variant.id === activeTrainingVariant.id)
      || (!Array.isArray(event.lineup.variants) && activeTrainingVariant.id === 'training-1')
    )
  );
  const isPublished = event.lineupPublished && (
    isTurnier || lineup.publishedVariantId === activeTrainingVariant?.id
  );
  const addTrainingVariant = (copyActive = false) => {
    if (!activeTrainingVariant) return;
    const newId = newGroupId('training');
    const variantData = copyActive
      ? JSON.parse(JSON.stringify(activeTrainingVariant))
      : emptyTrainingLineup();
    delete variantData.__meta;
    delete variantData.type;
    setLineup(prev => ({
      ...prev,
      variants: [...prev.variants, {
        ...variantData,
        id: newId,
        name: `Variante ${prev.variants.length + 1}`,
      }],
    }));
    setActiveTrainingVariantId(newId);
    setLineupMeta(null);
  };
  const removeActiveTrainingVariant = () => {
    if (!activeTrainingVariant || lineup.variants.length <= 1) return;
    const currentIndex = lineup.variants.findIndex(variant => variant.id === activeTrainingVariant.id);
    const nextVariant = lineup.variants[currentIndex === 0 ? 1 : currentIndex - 1];
    const remainingVariants = lineup.variants
      .filter(variant => variant.id !== activeTrainingVariant.id)
      .map((variant, index) => ({ ...variant, name: `Variante ${index + 1}` }));
    setDeletedTrainingVariantIds(prev => prev.includes(activeTrainingVariant.id)
      ? prev
      : [...prev, activeTrainingVariant.id]);
    setLineup(prev => ({
      ...prev,
      variants: remainingVariants,
      publishedVariantId: prev.publishedVariantId === activeTrainingVariant.id
        ? null
        : prev.publishedVariantId,
    }));
    setActiveTrainingVariantId(nextVariant.id);
    const nextActiveVariant = remainingVariants.find(variant => variant.id === nextVariant.id);
    setLineupMeta(getLineupMeta(nextActiveVariant));
  };
  const isTrainingVariantDirty = (variant) =>
    savedTrainingVariants[variant.id] !== JSON.stringify(variant);
  const saveActiveTrainingVariant = async () => {
    const savedVariant = withLineupMeta(sanitizeLineup(activeTrainingVariant), lineupMeta);
    const succeeded = await onSave({
      variantId: activeTrainingVariant.id,
      variant: savedVariant,
      variantOrder: lineup.variants.map(variant => variant.id),
      deletedVariantIds: deletedTrainingVariantIds,
    });
    if (succeeded === false) return;
    setSavedTrainingVariants(prev => {
      const next = { ...prev };
      deletedTrainingVariantIds.forEach(id => { delete next[id]; });
      lineup.variants.forEach(variant => {
        if (variant.id === activeTrainingVariant.id) {
          next[variant.id] = JSON.stringify(savedVariant);
        } else if (next[variant.id]) {
          const previouslySaved = JSON.parse(next[variant.id]);
          next[variant.id] = JSON.stringify({ ...previouslySaved, name: variant.name });
        }
      });
      return next;
    });
    setDeletedTrainingVariantIds([]);
  };
  const displayNameFor = (identifier) => {
    const p = getPlayerObj(identifier);
    if (!p) return identifier;
    const nick = (p.nickname || '').trim();
    if (nick) return nick;
    const first = (p.firstName || '').trim();
    return first ? first.split(/\s+/)[0] : identifier;
  };
  const exportTrainingLineup = async () => {
    if (!lineup || lineup.type !== 'training') return;
    const isIOS = typeof navigator !== 'undefined' && (
      /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
    const markExportDone = () => { setExportDone(true); window.setTimeout(() => setExportDone(false), 1800); };
    const tryLegacyCopy = (value) => {
      try {
        const ta = document.createElement('textarea');
        ta.value = value; ta.setAttribute('readonly', '');
        ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.left = '0';
        ta.style.width = '1px'; ta.style.height = '1px'; ta.style.opacity = '0.01';
        ta.style.pointerEvents = 'none'; ta.style.fontSize = '16px';
        document.body.appendChild(ta); ta.focus(); ta.select(); ta.setSelectionRange(0, ta.value.length);
        const ok = document.execCommand('copy'); document.body.removeChild(ta); return !!ok;
      } catch { return false; }
    };
    const tryWhatsAppShare = (value) => {
      try { const url = `https://wa.me/?text=${encodeURIComponent(value)}`; const w = window.open(url, '_blank', 'noopener,noreferrer'); return !!w; } catch { return false; }
    };
    const formatTeam = (teamKey, title) => {
      const colorEmoji = teamKey === 'teamBlue' ? '🔵' : '⚪';
      const rows = [
        `*${colorEmoji} ${title}*`,
        '    🧤 _Deckel_',
        ...(activeTrainingLineup[teamKey]?.Deckel || []).map(id => `        ${displayNameFor(id)}`),
        '    🛡️ _Verteidigung_',
        ...(activeTrainingLineup[teamKey]?.Verteidigung || []).map(id => `        ${displayNameFor(id)}`),
        '    ⚽ _Sturm_',
        ...(activeTrainingLineup[teamKey]?.Sturm || []).map(id => `        ${displayNameFor(id)}`),
      ];
      return rows.join('\n');
    };
    const header = `Einteilung für ${event.name} am ${new Date(event.date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    const text = [header, `*${activeTrainingVariant.name}*`, formatTeam('teamBlue', 'blau'), formatTeam('teamWhite', 'weiß')].join('\n\n');
    const copyText = text.replace(/\n/g, '\r\n');
    if (isIOS) {
      if (typeof navigator?.share === 'function') { try { await navigator.share({ text }); markExportDone(); return; } catch {} }
      if (tryWhatsAppShare(text)) { markExportDone(); return; }
      if (tryLegacyCopy(copyText)) { markExportDone(); return; }
    }
    try { if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(copyText); markExportDone(); return; } } catch {}
    if (tryLegacyCopy(copyText)) { markExportDone(); return; }
    if (typeof navigator?.share === 'function') { try { await navigator.share({ text }); markExportDone(); return; } catch {} }
    window.alert('Export fehlgeschlagen. Bitte in einem anderen Browser versuchen.');
  };

  const AutoLineupChoiceDialog = () => {
    const hasBench = autoLineupChoice?.benchPlayers?.length > 0;
    return (
      <Dialog open={!!autoLineupChoice} onClose={() => setAutoLineupChoice(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Automatische Aufstellung</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Es gibt bereits eine gespeicherte Aufstellung. Was soll gemacht werden?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2.5, alignItems: 'stretch' }}>
          <Button
            variant="contained"
            onClick={() => handleAutoLineupDialogChoice('new')}
            sx={{ textTransform: 'none', justifyContent: 'flex-start', fontWeight: 600 }}
          >
            Neue Aufstellung automatisch erstellen
          </Button>
          {hasBench && (
            <Button
              variant="outlined"
              onClick={() => handleAutoLineupDialogChoice('bench')}
              sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
            >
              {autoLineupChoice.benchPlayers.length} Bankspieler zur bestehenden Aufstellung hinzufügen
            </Button>
          )}
          <Button
            variant="text"
            onClick={() => handleAutoLineupDialogChoice('keep')}
            sx={{ textTransform: 'none', color: 'text.secondary', justifyContent: 'flex-start' }}
          >
            Bestehende Aufstellung behalten
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
    <AutoLineupChoiceDialog />
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#0D47A1',
          pt: 'env(safe-area-inset-top)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 'auto', sm: 56 }, px: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 0 }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 0.75, sm: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <LineupIcon sx={{ color: '#fff' }} />
            <Typography variant="subtitle1" fontWeight={700} color="#fff" sx={{ flex: 1, minWidth: 0 }} noWrap>
              Aufstellung · {event.name}
            </Typography>
            {canEdit && lineupPreferences.sampleCount > 0 && (
              <Tooltip title={`Die Auto-Aufstellung nutzt ${lineupPreferences.sampleCount} manuell angepasste fruhere Aufstellungen desselben Typs nur als weiche Praferenz.`}>
                <Chip label={`Historie ${lineupPreferences.sampleCount}`} size="small" sx={{ bgcolor: '#FFF3E0', color: '#8A4B00', fontWeight: 700 }} />
              </Tooltip>
            )}
            {isPublished && (
              <Chip label="Veröffentlicht ✓" size="small" sx={{ bgcolor: '#4CAF50', color: '#fff', fontWeight: 700, mr: { xs: 0, sm: 1 } }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: '100%', flexWrap: 'wrap' }}>
            {canEdit && (
              <>
                <Tooltip title="Automatisch befüllen">
                  <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.85)' }}
                    onClick={isTurnier ? autoTournament : handleAutoTraining}>
                    <PsychologyIcon />
                  </IconButton>
                </Tooltip>
                {!isTurnier && (
                  <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />}
                    sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', textTransform: 'none' }}
                    onClick={exportTrainingLineup}>
                    {exportDone ? 'Kopiert ✓' : 'Export'}
                  </Button>
                )}
                <Button size="small" variant="outlined"
                  sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)', textTransform: 'none' }}
                  onClick={() => isTurnier
                    ? onSave(withLineupMeta(sanitizeLineup(lineup), lineupMeta))
                    : saveActiveTrainingVariant()}>
                  Speichern
                </Button>
                <Tooltip title={!canPublishActiveTrainingVariant
                  ? 'Speichere die Variante vor der Veröffentlichung'
                  : isPublished ? 'Veröffentlichung zurückziehen' : 'Aufstellung veröffentlichen'}>
                  <span>
                  <IconButton size="small" disabled={!canPublishActiveTrainingVariant}
                    sx={{ color: isPublished ? '#FFE082' : 'rgba(255,255,255,0.85)' }}
                    onClick={() => onPublish(isTurnier ? undefined : activeTrainingVariant?.id)}>
                    {isPublished ? <LockIcon /> : <LockOpenIcon />}
                  </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.85)' }} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {isMobileLayout && (
        <Box sx={{ bgcolor: '#f0f4f8', borderBottom: '1px solid #e0e7ef', px: 1, py: 0.5 }}>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 11.5, color: '#0D47A1', lineHeight: 1.2 }}>
            Einteilung für {event.name} am {new Date(event.date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>
      )}

      <Box sx={{ p: { xs: 1, sm: 2 }, overflowY: 'auto', flexGrow: 1,
        display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1.25, md: 2 }, alignItems: { xs: 'stretch', md: 'flex-start' } }}>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {!isMobileLayout && (
            <Box sx={{ display: 'flex', gap: 1.2, mb: 1.3, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: 11 }}>Position:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', bgcolor: '#E8F5E9' }}>
                  <LooksOneIcon sx={{ fontSize: 12, color: '#2E7D32' }} />
                </Box>
                <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 700, fontSize: 11 }}>Primar</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', bgcolor: '#FFF3E0' }}>
                  <LooksTwoIcon sx={{ fontSize: 12, color: '#E65100' }} />
                </Box>
                <Typography variant="caption" sx={{ color: '#E65100', fontWeight: 700, fontSize: 11 }}>Sekundar</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#9E9E9E', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#607D8B', fontWeight: 700, fontSize: 11 }}>Andere</Typography>
              </Box>
              {tapMoveEnabled && (
                <Chip size="small"
                  label={tapSelection ? 'Spieler ausgewählt: Ziel antippen' : 'Touch-Modus: Spieler antippen, dann Ziel antippen'}
                  sx={{ height: 22, fontSize: 11, bgcolor: '#E3F2FD', color: '#0D47A1', fontWeight: 600 }} />
              )}
            </Box>
          )}

          {!isTurnier && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
                {lineup.variants.map(variant => {
                  const isActive = activeTrainingVariant?.id === variant.id;
                  const isPublishedVariant = lineup.publishedVariantId === variant.id;
                  const isDirty = isTrainingVariantDirty(variant);
                  return (
                    <Chip
                      key={variant.id}
                      label={`${variant.name}${isDirty ? ' *' : ''}`}
                      clickable
                      size="small"
                      title={[
                        isDirty ? 'Änderungen noch nicht gespeichert' : 'Gespeichert',
                        isPublishedVariant ? 'Diese Variante ist veröffentlicht' : '',
                      ].filter(Boolean).join(' · ')}
                      onClick={() => {
                        setActiveTrainingVariantId(variant.id);
                        setLineupMeta(getLineupMeta(variant));
                      }}
                      color={isPublishedVariant ? 'success' : isActive ? 'primary' : 'default'}
                      variant={isActive ? 'filled' : 'outlined'}
                      sx={{ fontWeight: isActive ? 700 : 400 }}
                    />
                  );
                })}
                {canEdit && (
                  <>
                    <Tooltip title="Leere Variante hinzufügen">
                      <IconButton
                        size="small"
                        aria-label="Variante hinzufügen"
                        onClick={() => addTrainingVariant(false)}
                        sx={{ bgcolor: '#E3F2FD', color: '#0D47A1', width: 28, height: 28 }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Aktuelle Variante kopieren">
                      <IconButton
                        size="small"
                        aria-label="Variante kopieren"
                        onClick={() => addTrainingVariant(true)}
                        sx={{ bgcolor: '#E3F2FD', color: '#0D47A1', width: 28, height: 28 }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={lineup.variants.length <= 1 ? 'Mindestens eine Variante muss bestehen bleiben' : 'Aktuelle Variante entfernen'}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Variante entfernen"
                          disabled={lineup.variants.length <= 1}
                          onClick={removeActiveTrainingVariant}
                          sx={{ bgcolor: '#FFEBEE', color: '#C62828', width: 28, height: 28 }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
              {isMobileLayout && (
                <Tabs value={mobileTab} onChange={(_e, v) => setMobileTab(v)} variant="fullWidth"
                  sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.25, fontSize: 11, fontWeight: 700 } }}>
                  <Tab value={0} label="Aufstellung" />
                  <Tab value={1} label="Teamvergleich" />
                </Tabs>
              )}
              {(!isMobileLayout || mobileTab === 0) && (
                <>
                  {isMobileLayout && mobileMove && (
                    <Chip size="small" label="Spieler gewählt: Ziel antippen"
                      onDelete={() => setMobileMove(null)}
                      sx={{ mb: 0.4, height: 20, fontSize: 10, bgcolor: '#E3F2FD', color: '#0D47A1', fontWeight: 600 }} />
                  )}
                  {['teamBlue', 'teamWhite'].map(teamKey => {
                    const color = teamKey === 'teamBlue' ? TEAM_COLORS.blue : TEAM_COLORS.white;
                    const allTeamEmails = LINEUP_POSITIONS.flatMap(pos => activeTrainingLineup[teamKey][pos] || []);
                    return (
                      <Box key={teamKey} sx={{ width: '100%', border: `2px solid ${color}40`, borderRadius: 2, p: isMobileLayout ? 0.75 : 1.5,
                        background: `linear-gradient(135deg, ${color}06 0%, transparent 100%)` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: isMobileLayout ? 0.5 : 1 }}>
                          <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                          <Typography variant="subtitle2" fontWeight={800} sx={{ color, fontSize: isMobileLayout ? 13 : 14 }}>
                            {TEAM_LABELS[teamKey === 'teamBlue' ? 'blue' : 'white']}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobileLayout ? 10 : 12 }}>({allTeamEmails.length} Spieler)</Typography>
                        </Box>
                        {isMobileLayout ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {LINEUP_POSITIONS.map(pos => {
                              const emails = activeTrainingLineup[teamKey]?.[pos] || [];
                              return (
                                <Box key={`${teamKey}-${pos}`}>
                                  <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.3 }}>{pos} ({emails.length})</Typography>
                                  <Box sx={{ mt: 0.35, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                    {emails.length === 0
                                      ? <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Keine Spieler</Typography>
                                      : emails.map(email => <MobilePlayerRow key={`${teamKey}-${pos}-${email}`} email={email} fromZone={{ zone: 'pos', team: teamKey, pos }} />)}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 0.9 }}>
                            {LINEUP_POSITIONS.map(pos => (
                              <DropZone key={pos} label={pos} emails={activeTrainingLineup[teamKey][pos] || []}
                                toZone={{ zone: 'pos', team: teamKey, pos }} accentColor={color} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                  {isMobileLayout && (
                    <Box sx={{ width: '100%', border: '2px dashed #e0e7ef', borderRadius: 2, p: 0.6, bgcolor: '#fafafa' }}>
                      <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.3 }}>Bank ({bench.length})</Typography>
                      <Box sx={{ mt: 0.3, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {bench.length === 0
                          ? <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Alle Spieler eingeteilt</Typography>
                          : bench.map(email => <MobilePlayerRow key={`bench-${email}`} email={email} fromZone={{ zone: 'bench' }} />)}
                      </Box>
                    </Box>
                  )}
                </>
              )}
              {(!isMobileLayout || mobileTab === 1) && (
                <TeamStatsComparison
                  bluePositions={activeTrainingLineup.teamBlue}
                  whitePositions={activeTrainingLineup.teamWhite}
                />
              )}
            </Box>
          )}

          {isTurnier && (
            <Box>
              <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {(lineup.games || []).map((g, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <Chip label={g.name} clickable size="small" onClick={() => setActiveGame(i)}
                      variant={activeGame === i ? 'filled' : 'outlined'} color={activeGame === i ? 'primary' : 'default'}
                      sx={{ fontWeight: activeGame === i ? 700 : 400 }} />
                    {canEdit && (
                      <IconButton size="small" sx={{ width: 18, height: 18, color: '#C62828' }}
                        onClick={() => {
                          updateLineup(prev => {
                            const ng = prev.games.filter((_, gi) => gi !== i);
                            return { ...prev, games: ng };
                          }, { manual: true });
                          setActiveGame(a => Math.min(a, Math.max(0, lineup.games.length - 2)));
                        }}>
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {canEdit && (lineup.games || []).length < TOURNAMENT_MAX_GAMES && (
                  <Tooltip title="Spiel hinzufügen">
                    <IconButton size="small" sx={{ bgcolor: '#E3F2FD', color: '#0D47A1', width: 28, height: 28 }}
                      onClick={() => {
                        updateLineup(prev => {
                          const ng = [...(prev.games || []), emptyGame(prev.games.length)];
                          setActiveGame(ng.length - 1);
                          return { ...prev, games: ng };
                        }, { manual: true });
                      }}>
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                {(lineup.games || []).length === 0 && (
                  <Typography variant="caption" color="text.secondary">Kein Spiel. Klicke + um ein Spiel hinzuzufügen.</Typography>
                )}
              </Box>
              {(lineup.games || []).length > 0 && lineup.games[activeGame] && (() => {
                const g = lineup.games[activeGame];
                const allGameEmails = LINEUP_POSITIONS.flatMap(pos => g[pos] || []);
                const GroupControls = ({ pos, group }) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <Select size="small" value={group.mode} disabled={!canEdit}
                      onChange={(e) => setGroupMode(pos, group.id, e.target.value)}
                      sx={{ fontSize: 11, height: 24, '& .MuiSelect-select': { py: 0, pl: 0.75 } }}>
                      {SWAP_MODE_KEYS.map(key => <MenuItem key={key} value={key} sx={{ fontSize: 12 }}>{SWAP_MODES[key].label}</MenuItem>)}
                    </Select>
                    {canEdit && (
                      <IconButton size="small" onClick={() => removeGroup(pos, group.id)} title="Gruppe entfernen" sx={{ p: 0.3 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                );
                return (
                  <Box sx={{ width: '100%', border: '2px solid #0D47A130', borderRadius: 2, p: 1.5 }}>
                    {isMobileLayout ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {mobileMove && (
                          <Chip size="small" label="Spieler gewählt: Ziel antippen" onDelete={() => setMobileMove(null)}
                            sx={{ mb: 0.4, height: 20, fontSize: 10, bgcolor: '#E3F2FD', color: '#0D47A1', fontWeight: 600 }} />
                        )}
                        {LINEUP_POSITIONS.map(pos => {
                          const groups = getSwapGroups(g, pos);
                          const ungrouped = getUngroupedPlayers(g, pos);
                          return (
                            <Box key={pos}>
                              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.3 }}>
                                {pos} ({(g[pos] || []).length}/{TOURNAMENT_MAX_PER_POS})
                              </Typography>
                              <Box sx={{ mt: 0.35, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                {ungrouped.map(email => <MobilePlayerRow key={`${activeGame}-${pos}-${email}`} email={email} fromZone={{ zone: 'pos', pos, gameIdx: activeGame }} />)}
                                {groups.map((group, idx) => (
                                  <Box key={group.id} sx={{ border: `1px solid ${groupColor(idx)}`, borderRadius: 2, p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <MarkBadge mark={{ letter: groupLetter(idx), color: groupColor(idx), mode: group.mode }} />
                                      <GroupControls pos={pos} group={group} />
                                    </Box>
                                    {group.players.map((email, i) => (
                                      <Box key={email}>
                                        {slotCaption(group.mode, i) && (
                                          <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>{slotCaption(group.mode, i)}</Typography>
                                        )}
                                        <MobilePlayerRow email={email} fromZone={{ zone: 'pos', pos, gameIdx: activeGame, groupId: group.id }} />
                                      </Box>
                                    ))}
                                  </Box>
                                ))}
                                {(g[pos] || []).length === 0 && groups.length === 0
                                  && <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Keine Spieler</Typography>}
                                {canEdit && (
                                  <Button size="small" startIcon={<AddIcon />} onClick={() => addGroup(pos)} sx={{ textTransform: 'none', alignSelf: 'flex-start', fontSize: 11 }}>
                                    Gruppe
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 800, color: 'text.secondary', letterSpacing: 0.3 }}>Reserve ({bench.length})</Typography>
                        <Box sx={{ mt: 0.35, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                          {bench.length === 0
                            ? <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>Alle Spieler eingeteilt</Typography>
                            : bench.map(email => <MobilePlayerRow key={`bench-game-${activeGame}-${email}`} email={email} fromZone={{ zone: 'bench' }} />)}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 0.9, alignItems: 'start' }}>
                        {LINEUP_POSITIONS.map(pos => {
                          const groups = getSwapGroups(g, pos);
                          const ungrouped = getUngroupedPlayers(g, pos);
                          return (
                            <Box key={pos} sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                              <DropZone label={pos} emails={ungrouped}
                                toZone={{ zone: 'pos', pos, gameIdx: activeGame }}
                                headerExtra={<Typography sx={{ fontSize: 10, color: 'text.secondary', ml: 'auto' }}>{(g[pos] || []).length}/{TOURNAMENT_MAX_PER_POS}</Typography>}
                                accentColor="#0D47A1" />
                              {groups.map((group, idx) => (
                                <DropZone key={group.id} label={`Gruppe ${groupLetter(idx)}`} emails={group.players}
                                  toZone={{ zone: 'pos', pos, gameIdx: activeGame, groupId: group.id }}
                                  posForFit={pos} maxSlots={SWAP_MODES[group.mode].size}
                                  slotLabels={SWAP_MODES[group.mode].slots} slotMode={group.mode}
                                  headerExtra={<GroupControls pos={pos} group={group} />}
                                  accentColor={groupColor(idx)} />
                              ))}
                              {canEdit && (
                                <Button size="small" startIcon={<AddIcon />} onClick={() => addGroup(pos)} sx={{ textTransform: 'none', fontSize: 11 }}>
                                  Wechselgruppe
                                </Button>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    <TeamStats allEmails={allGameEmails} accentColor="#0D47A1" />
                  </Box>
                );
              })()}
            </Box>
          )}
        </Box>

        {!isMobileLayout && (
          <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
            <BenchPanel />
          </Box>
        )}
      </Box>
    </Dialog>
    </>
  );
}
